import { workerHttpUrl } from './worker-config';

export interface SessionUser {
  id: string;
  username: string;
  xp: number;
  level: number;
  emailVerified: boolean;
}

export interface RatingInfo {
  user_id: string;
  rating: number;
  wins: number;
  losses: number;
  ties: number;
}

export interface Preferences {
  reducedMotion: boolean;
  highContrast: boolean;
  notifyFriendRequests: boolean;
  notifyDirectChallenges: boolean;
  notifyMatchResults: boolean;
}

export const DEFAULT_PREFERENCES: Preferences = {
  reducedMotion: false,
  highContrast: false,
  notifyFriendRequests: true,
  notifyDirectChallenges: true,
  notifyMatchResults: true,
};

const TOKEN_KEY = 'rw_session_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** An error from a completed HTTP response, carrying its status so callers can distinguish e.g. a 401 from a network failure. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${workerHttpUrl()}${path}`, { ...options, headers });
  const data = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new ApiError(data.error ?? `request failed with status ${response.status}`, response.status);
  return data;
}

export async function register(username: string, password: string, email: string): Promise<{ token: string; user: SessionUser }> {
  const result = await api<{ token: string; user: SessionUser }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ username, password, email }),
  });
  setToken(result.token);
  return result;
}

export async function login(username: string, password: string): Promise<{ token: string; user: SessionUser }> {
  const result = await api<{ token: string; user: SessionUser }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  setToken(result.token);
  return result;
}

/** No real email sending is wired up yet — the "sent" link only ever reaches the account holder via the worker's own console log (see auth-routes.ts's stubSendAuthEmail). These calls still work end to end for that link once it's followed. */
export async function verifyEmail(token: string): Promise<void> {
  await api('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) });
}

export async function resendVerification(): Promise<{ alreadyVerified?: boolean }> {
  return api('/api/auth/resend-verification', { method: 'POST' });
}

export async function forgotPassword(email: string): Promise<void> {
  await api('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await api('/api/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) });
}

export async function logout(): Promise<void> {
  try {
    await api('/api/auth/logout', { method: 'POST' });
  } finally {
    clearToken();
  }
}

export async function fetchMe(): Promise<{ user: SessionUser; rating: RatingInfo; preferences: Preferences } | null> {
  if (!getToken()) return null;
  try {
    return await api<{ user: SessionUser; rating: RatingInfo; preferences: Preferences }>('/api/me');
  } catch (error) {
    // Only an actual auth rejection invalidates the stored session. A network
    // error (offline, worker restarting) must not silently sign the player out
    // of every device — leave the token in place and just report "no session
    // right now" so a retry can recover.
    if (error instanceof ApiError && error.status === 401) clearToken();
    return null;
  }
}

/**
 * Mints a single-use WebSocket connection ticket. The socket URL carries this,
 * not the session token, so the long-lived token never lands in a URL/log.
 */
export async function mintConnectionTicket(): Promise<string> {
  const { ticket } = await api<{ ticket: string }>('/api/connection-ticket', { method: 'POST' });
  return ticket;
}

/** Reflects preferences onto the document root so the CSS can honour them. */
export function applyPreferences(prefs: Preferences): void {
  const root = document.documentElement;
  root.dataset.reducedMotion = String(prefs.reducedMotion);
  root.dataset.highContrast = String(prefs.highContrast);
}

export async function fetchPreferences(): Promise<Preferences> {
  if (!getToken()) return { ...DEFAULT_PREFERENCES };
  try {
    const { preferences } = await api<{ preferences: Preferences }>('/api/account/preferences');
    return preferences;
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export async function savePreferences(prefs: Preferences): Promise<Preferences> {
  const { preferences } = await api<{ preferences: Preferences }>('/api/account/preferences', {
    method: 'POST',
    body: JSON.stringify({ preferences: prefs }),
  });
  return preferences;
}

/** Fetches and applies preferences on startup, so a returning player's settings take effect immediately. */
export async function loadAndApplyPreferences(): Promise<void> {
  applyPreferences(await fetchPreferences());
}

export { api as authorizedFetch };
